import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { AuxiaResponse } from "src/pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment";
import { isAuxiaServedPage } from "src/pages/software/jira/service-management/auxia/is-auxia-served-page";
import {
    convertDateTimeToInteractionTimeMicros,
    sendAnalyticsToAuxia
} from "src/pages/software/jira/service-management/auxia/send-analytics-to-auxia";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import AccountCreation, {
    Input,
    Login,
    Social,
    Submit
} from "@/components/signup/account-creation";
import { AnimatedSocialProof } from "@/components/signup/bxp-ui-signup/src/components/signup-form/components";
import { getLoginUrl } from "@/components/signup/utils";
import {
    useJiraServiceManagementIntent,
    useUserContext
} from "@/components/user-context";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { Cookie } from "@/utils/cookie";
import { useLocation } from "@/utils/location-provider";

import { AuxiaSocial } from "../components/paid-jsm-account-creation-social-auxia";
import { AuxiaSubmit } from "../components/paid-jsm-account-creation-submit-auxia";

// eslint-disable-next-line complexity
export const PaidAccountCreation = ({
    darkSolidTheme,
    showFourSocialButtons,
    auxiaResponse
}: {
    darkSolidTheme?: boolean;
    showFourSocialButtons?: boolean;
    auxiaResponse?: AuxiaResponse | null;
}) => {
    const { product } = useUserContext();
    const i18n = useIntl();
    const location = useLocation();

    const isAuxiaPage =
        useJiraServiceManagementIntent() && isAuxiaServedPage(auxiaResponse);

    const logInLink = (text: ReactNode) => (
        <Link
            text={text as unknown as string}
            aria-label={text as unknown as string}
            type="inline"
            href={getLoginUrl(location)}
            onClick={() => {
                if (isAuxiaPage) {
                    const anonymousId = decodeURIComponent(
                        Cookie.readCookie("ajs_anonymous_id") || ""
                    ).replace(/['"]+/g, "");

                    sendAnalyticsToAuxia({
                        anonymousId: anonymousId,
                        treatmentTrackingId: auxiaResponse.treatmentTrackingId,
                        treatmentId: auxiaResponse.treatmentId,
                        interactionType: "CLICKED",
                        interactionTimeMicros:
                            convertDateTimeToInteractionTimeMicros(Date.now()),
                        actionName: "login_button"
                    });
                }
            }}
        />
    );

    const loginText = i18n.formatMessage(
        {
            id: "BxpUiSignup.SignupForm.existingProduct.confluence.embedded",
            defaultMessage:
                "Trying to access {product}? <logInLink>Log in</logInLink>"
        },
        {
            product: product?.description,
            logInLink
        }
    );

    return (
        <AccountCreation
            Input={
                <div
                    css={[
                        darkSolidTheme && whiteTextCss,
                        !darkSolidTheme && darkGrayTextCss
                    ]}
                >
                    <Input />
                </div>
            }
            Login={
                <div
                    css={[loginSocialProofCss, darkSolidTheme && whiteLinkCss]}
                >
                    <AnimatedSocialProof />
                    <Login loginText={loginText} />
                </div>
            }
            Social={
                <div css={[showFourSocialButtons && socialWrapperStyle]}>
                    {!isAuxiaPage && !showFourSocialButtons && (
                        <Social
                            providers={{
                                google: "Google",
                                microsoft: "Microsoft"
                            }}
                        />
                    )}
                    {!isAuxiaPage && showFourSocialButtons && <Social />}
                    {isAuxiaPage && !showFourSocialButtons && (
                        <AuxiaSocial
                            providers={{
                                google: "Google",
                                microsoft: "Microsoft"
                            }}
                            auxiaResponse={auxiaResponse}
                        />
                    )}
                    {isAuxiaPage && showFourSocialButtons && <AuxiaSocial />}
                </div>
            }
            Submit={
                <div css={[darkSolidTheme && yellowButtonCss]}>
                    {!isAuxiaPage && <Submit />}
                    {isAuxiaPage && (
                        <AuxiaSubmit auxiaResponse={auxiaResponse} />
                    )}
                </div>
            }
        />
    );
};
const socialWrapperStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ul: {
        gridTemplateColumns: "repeat(4, 1fr)"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        borderRadius: "40px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        display: "none"
    }
});

const loginSocialProofCss = css({
    // only need top padding when social proof is present
    [media.above.md]: {
        paddingBlockStart: "2rem"
    }
});

const yellowButtonCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        backgroundColor: wacToken("color.extended.yellow400"),
        color: `${token("color.text")} !important`,
        ["&:hover"]: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            backgroundColor: "#FF8B00",
            color: `${token("color.text")} !important`
        }
    }
});

const whiteLinkCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        color: `${wacToken("color.neutral.light0")} !important`
    }
});

const whiteTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role='note'], div[role='alert']": {
        color: `${wacToken("color.neutral.light0")} !important`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "#account_creation_email_help_text": {
        color: wacToken("color.neutral.light0")
    }
});

const darkGrayTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role='note']": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#44546F"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role='alert']": {
        color: `${token("color.icon.accent.red")} !important`
    }
});
